import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  Alert,
  Grid
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { FormBuilderData } from '../FormBuilder';

interface ReviewStepProps {
  formData: FormBuilderData;
}

const QUESTION_TYPES = {
  yes_no: 'Sí / No / N/A',
  scale: 'Escala Numérica',
  text: 'Texto Libre',
  number: 'Número',
  select: 'Selección Única',
  multiple: 'Selección Múltiple',
  date: 'Fecha',
  time: 'Hora'
};

const ReviewStep: React.FC<ReviewStepProps> = ({ formData }) => {
  const { formInfo, sections, categories, questions } = formData;

  // Calcular estadísticas por sección
  const getSectionQuestions = (sectionId: number) => {
    return questions.filter(q => q.section_id === sectionId);
  };

  // Calcular ponderación por categoría
  const getCategoryPonderacion = (categoryId: number) => {
    const catQuestions = questions.filter(q => q.category_id === categoryId);
    return catQuestions.reduce((sum, q) => sum + q.ponderacion, 0);
  };

  const totalQuestions = questions.length;
  const obligatoryQuestions = questions.filter(q => q.obligatoria).length;
  const questionsWithEvidence = questions.filter(q => q.permite_evidencia).length;

  // Validaciones
  const allCategoriesValid = categories.every(cat => {
    const ponderacion = getCategoryPonderacion(cat.id!);
    return Math.abs(ponderacion - 100) < 0.01;
  });

  const hasQuestions = questions.length > 0;
  const hasCategories = categories.length > 0;
  const hasSections = sections?.length > 0 || false;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Revisión Final
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Revise toda la información antes de guardar el formulario.
      </Typography>

      {/* Validaciones */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: allCategoriesValid && hasQuestions && hasCategories && hasSections ? 'success.light' : 'warning.light' }}>
        <Box display="flex" alignItems="center" gap={2}>
          {allCategoriesValid && hasQuestions && hasCategories && hasSections ? (
            <>
              <CheckIcon color="success" />
              <Typography variant="subtitle1" color="success.dark">
                ✓ El formulario está listo para ser guardado
              </Typography>
            </>
          ) : (
            <>
              <WarningIcon color="warning" />
              <Typography variant="subtitle1" color="warning.dark">
                ! Hay validaciones pendientes
              </Typography>
            </>
          )}
        </Box>
        
        {(!hasCategories || !hasQuestions || !allCategoriesValid || !hasSections) && (
          <Box mt={2}>
            {!hasSections && (
              <Alert severity="error" sx={{ mb: 1 }}>
                Debe agregar al menos una sección
              </Alert>
            )}
            {!hasCategories && (
              <Alert severity="error" sx={{ mb: 1 }}>
                Debe agregar al menos una categoría
              </Alert>
            )}
            {!hasQuestions && (
              <Alert severity="error" sx={{ mb: 1 }}>
                Debe agregar al menos una pregunta
              </Alert>
            )}
            {!allCategoriesValid && hasQuestions && (
              <Alert severity="warning">
                Algunas categorías no suman 100% de ponderación
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      {/* Información básica */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Información Básica
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary">
              Nombre
            </Typography>
            <Typography variant="body1">
              {formInfo.nombre || '(Sin nombre)'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary">
              Descripción
            </Typography>
            <Typography variant="body1">
              {formInfo.descripcion || '(Sin descripción)'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" gap={1}>
              {formInfo.es_template && (
                <Chip label="Es Plantilla" color="info" size="small" />
              )}
              {formInfo.version && (
                <Chip label={`Versión ${formInfo.version}`} variant="outlined" size="small" />
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Estadísticas generales */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Estadísticas Generales
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Box textAlign="center">
              <Typography variant="h4" color="primary">
                {sections?.length || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Secciones
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box textAlign="center">
              <Typography variant="h4" color="primary">
                {categories.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Categorías
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box textAlign="center">
              <Typography variant="h4" color="primary">
                {totalQuestions}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Preguntas
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box textAlign="center">
              <Typography variant="h4" color="error">
                {obligatoryQuestions}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Obligatorias
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box textAlign="center">
              <Typography variant="h4" color="success">
                {questionsWithEvidence}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Con Evidencia
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Detalle por sección */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Detalle por Sección
        </Typography>
        
        <List>
          {sections && sections.map((section, index) => {
            const sectionQuestions = getSectionQuestions(section.id!);
            
            return (
              <React.Fragment key={section.id || index}>
                {index > 0 && <Divider />}
                <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                  <Box display="flex" alignItems="center" gap={2} width="100%" mb={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 40,
                        bgcolor: section.color || '#2196F3',
                        borderRadius: 1
                      }}
                    />
                    <Box flex={1}>
                      <Typography variant="subtitle1">
                        {section.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {section.description || 'Sin descripción'}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${sectionQuestions.length} preguntas`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  
                  {sectionQuestions.length > 0 && (
                    <Box pl={3} width="100%">
                      <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                        Preguntas:
                      </Typography>
                      <List dense>
                        {sectionQuestions
                          .sort((a, b) => a.orden - b.orden)
                          .map((question, qIndex) => {
                            const category = categories.find(c => c.id === question.category_id);
                            
                            return (
                              <ListItem key={question.id || qIndex} sx={{ py: 0.5 }}>
                                <ListItemText
                                  primary={
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Typography variant="body2">
                                        {qIndex + 1}. {question.texto}
                                      </Typography>
                                    </Box>
                                  }
                                  secondary={
                                    <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                                      <Chip
                                        label={QUESTION_TYPES[question.tipo]}
                                        size="small"
                                        variant="outlined"
                                      />
                                      {category && (
                                        <Chip
                                          label={`Categoría: ${category.nombre}`}
                                          size="small"
                                          sx={{
                                            bgcolor: category.color || '#2196F3',
                                            color: 'white'
                                          }}
                                        />
                                      )}
                                      <Chip
                                        label={`${question.ponderacion}%`}
                                        size="small"
                                        color="primary"
                                      />
                                      {question.obligatoria && (
                                        <Chip label="Obligatoria" size="small" color="error" />
                                      )}
                                    </Box>
                                  }
                                  secondaryTypographyProps={{ component: 'div' }}
                                />
                              </ListItem>
                            );
                          })}
                      </List>
                    </Box>
                  )}
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      </Paper>

      {/* Información adicional */}
      <Box mt={3}>
        <Alert severity="info" icon={<InfoIcon />}>
          <Typography variant="body2">
            Al guardar el formulario, se crearán todas las categorías y preguntas configuradas.
            {formInfo.id && ' Los cambios se guardarán en el formulario existente.'}
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
};

export default ReviewStep;
